export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              Material waste is a silent profit killer. Every sheet of plywood over-ordered, every stick of lumber left unused, and every emergency run to the supplier chips away at your bottom line.
            </p>

            <p>
              At <strong>Dinari Designs</strong>, we eliminate guesswork with <strong>digital Bluebeam Revu takeoffs</strong>. Here's why precision material quantification is the smartest investment you can make for your formwork project:
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-calculator-line text-white text-xl"></i>
                </span>
                The Problem with Manual Counting
              </h2>
              <p className="text-gray-700 mb-0">
                Traditional "tape measure and notepad" takeoffs are prone to human error. A missed measurement here, a forgotten penetration there—and suddenly you're either short on materials (causing delays) or drowning in excess inventory (wasting money). Neither scenario is acceptable in today's competitive construction environment.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-ruler-2-line text-white text-xl"></i>
                </span>
                Digital Precision with Bluebeam Revu
              </h2>
              <p className="text-gray-700 mb-0">
                Using <strong>Bluebeam Revu</strong>, we perform hyper-accurate digital measurements directly from your architectural and structural plans. Every slab edge, bulkhead, and column location is measured to the millimeter. The result? A <strong>Bill of Quantities (BOQ)</strong> that you can trust—down to the last board foot.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                </span>
                Reducing Over-Ordering and Waste
              </h2>
              <p className="text-gray-700 mb-0">
                Accurate takeoffs mean you order exactly what you need. No more "just in case" padding that inflates costs. No more leftover materials sitting in your yard. Our clients consistently report <strong>10-15% reductions in material costs</strong> simply by switching from manual to digital takeoffs.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-truck-line text-white text-xl"></i>
                </span>
                Eliminating Emergency Supplier Runs
              </h2>
              <p className="text-gray-700 mb-0">
                Running out of materials mid-pour is a nightmare. It costs time, money, and crew morale. With our <strong>Bluebeam takeoffs</strong>, you'll have confidence that your material order is complete before the first truck arrives. No surprises, no delays, no emergency trips to the lumber yard.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-file-chart-line text-white text-xl"></i>
                </span>
                Transparent, Itemized BOQs
              </h2>
              <p className="text-gray-700 mb-0">
                Our takeoffs aren't just numbers—they're detailed, itemized lists that break down every component: plywood sheets, dimensional lumber, hardware, and specialized materials. This transparency makes it easy to compare supplier quotes and negotiate better pricing.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Invest in precision. Our <strong>digital Bluebeam Revu takeoffs</strong> deliver the accuracy you need to control costs, reduce waste, and keep your project on budget.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
